'use client';

import { useState, useEffect } from 'react';

// 비디오 타입 정의
type Video = {
  id?: string;
  name: string;
  url: string;
  location: 'MAIN' | 'INTRO' | 'MENU';
  description?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export default function MenuVideo() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 비디오 데이터 가져오기
  useEffect(() => {
    setMounted(true);
    
    const fetchVideos = async () => {
      try {
        setLoading(true);
        // 이전 비디오 상태 초기화
        setVideos([]);
        
        const response = await fetch('/api/videos?location=MENU', {
          // 캐시 방지
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        
        if (!response.ok) {
          throw new Error('비디오 데이터를 불러오는데 실패했습니다');
        }
        
        const data = await response.json();
        console.log('MenuVideo: 받은 비디오 데이터', data);
        
        if (data.videos && data.videos.length > 0) {
          console.log('MenuVideo: 활성화된 비디오 찾음', data.videos);
          setVideos(data.videos);
        } else {
          console.log('MenuVideo: 비디오 데이터가 없음');
        }
      } catch (error) {
        console.error('비디오 로드 실패:', error);
        setError('비디오를 불러올 수 없습니다');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVideos();
    
    // 페이지 포커스가 돌아올 때 비디오 다시 로드
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchVideos();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 클라이언트 사이드 렌더링을 위한 체크
  if (!mounted) return null;

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {[1, 2, 3].map((index) => (
          <div key={index} className="relative rounded-lg overflow-hidden shadow-lg aspect-w-16 aspect-h-9 bg-gray-800 animate-pulse">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-white"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // 비디오가 없거나 에러가 있을 때
  if (videos.length === 0 || error) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {[1, 2, 3].map((index) => (
          <div key={index} className="relative rounded-lg overflow-hidden shadow-lg aspect-w-16 aspect-h-9 bg-gray-800">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white text-center p-4">
                <h3 className="text-xl font-semibold mb-2">
                  {error || '현재 재생할 비디오가 없습니다'}
                </h3>
                <p className="text-sm text-gray-300">
                  관리자 페이지에서 MENU 위치에 비디오를 등록해주세요
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // 비디오 표시
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
      {videos.map((video, index) => {
        const titles = ["든든한 식사", "추억의 분식", "디저트&음료"];
        const descriptions = [
          "푸짐하고 맛있는 돈키호테의 식사 메뉴로 든든한 하루를 완성하세요.",
          "어릴 적 즐겨 먹던 바로 그 맛, 돈키호테가 재현한 추억의 분식을 만나보세요.",
          "식사의 마무리를 완벽하게, 달콤한 디저트와 시원한 음료가 준비되어 있습니다."
        ];
        
        const videoIndex = index % 3;
        
        return (
          <div key={video.id || index} className="relative rounded-lg overflow-hidden shadow-lg group aspect-w-16 aspect-h-9">
            <video
              src={video.url}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-center items-center text-center p-4 transition-opacity duration-500 opacity-0 group-hover:opacity-100">
              <h3 className="text-2xl font-bold text-white">{video.name || titles[videoIndex]}</h3>
              <p className="text-sm text-gray-200 mt-2">
                {video.description || descriptions[videoIndex]}
              </p>
            </div>
          </div>
        );
      })}
      
      {/* 비디오가 3개 미만일 경우 플레이스홀더로 채우기 */}
      {videos.length < 3 && 
        Array.from({ length: 3 - videos.length }).map((_, index) => {
          const titles = ["든든한 식사", "추억의 분식", "디저트&음료"];
          const descriptions = [
            "푸짐하고 맛있는 돈키호테의 식사 메뉴로 든든한 하루를 완성하세요.",
            "어릴 적 즐겨 먹던 바로 그 맛, 돈키호테가 재현한 추억의 분식을 만나보세요.", 
            "식사의 마무리를 완벽하게, 달콤한 디저트와 시원한 음료가 준비되어 있습니다."
          ];
          
          const videoIndex = (videos.length + index) % 3;
          
          return (
            <div key={`placeholder-${index}`} className="relative rounded-lg overflow-hidden shadow-lg bg-gray-800">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-white text-center p-4">
                  <h3 className="text-xl font-semibold mb-2">{titles[videoIndex]}</h3>
                  <p className="text-sm text-gray-300">
                    관리자 페이지에서 MENU 위치에 비디오를 등록해주세요
                  </p>
                </div>
              </div>
            </div>
          );
        })
      }
    </div>
  );
}
